import { ref, computed, watch } from "vue";
import { debounce } from "lodash";

/**
 * Generic composable for paginated data fetching with infinite scroll.
 *
 * @param {Object} options
 * @param {Function} options.fetchFn - async (page, pageSize, searchQuery) => { items: [], total: number }
 * @param {number}  [options.pageSize=15]
 * @param {number}  [options.debounceMs=300]
 * @param {boolean} [options.autoFetch=true] - when false, skips the initial page-1 fetch on mount. Caller is expected to trigger via refetch().
 */
export function useInfiniteScroll({ fetchFn, pageSize = 15, debounceMs = 300, autoFetch = true }) {
  const items = ref([]);
  const loading = ref(false);
  const loadingMore = ref(false);
  const searchQuery = ref("");
  const total = ref(0);
  const currentPage = ref(1);

  const hasMore = computed(() => items.value.length < total.value);

  const fetchPage = async (page) => {
    try {
      const isFirstPage = page === 1;
      if (isFirstPage) {
        loading.value = true;
      } else {
        loadingMore.value = true;
      }

      const result = await fetchFn(page, pageSize, searchQuery.value);
      const newItems = result?.items || result?.lists || result?.sequences || [];
      const newTotal = result?.total ?? 0;

      if (isFirstPage) {
        items.value = newItems;
      } else {
        items.value = [...items.value, ...newItems];
      }
      total.value = newTotal;
      currentPage.value = page;
    } catch (error) {
      console.error("useInfiniteScroll fetch error:", error);
    } finally {
      loading.value = false;
      loadingMore.value = false;
    }
  };

  const loadMore = () => {
    if (!hasMore.value || loading.value || loadingMore.value) return;
    fetchPage(currentPage.value + 1);
  };

  const handleScroll = (event) => {
    const el = event.target;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 50;
    if (nearBottom) {
      loadMore();
    }
  };

  const reset = () => {
    items.value = [];
    total.value = 0;
    currentPage.value = 1;
    searchQuery.value = "";
    loading.value = false;
    loadingMore.value = false;
  };

  const refetch = () => {
    items.value = [];
    total.value = 0;
    currentPage.value = 1;
    fetchPage(1);
  };

  const debouncedSearch = debounce(() => {
    items.value = [];
    total.value = 0;
    currentPage.value = 1;
    fetchPage(1);
  }, debounceMs);

  watch(searchQuery, () => {
    debouncedSearch();
  });

  if (autoFetch) {
    fetchPage(1);
  }

  return {
    items,
    loading,
    loadingMore,
    searchQuery,
    hasMore,
    total,
    reset,
    refetch,
    loadMore,
    handleScroll,
  };
}
